import React from 'react';
import { Link, useNavigate } from 'react-router-dom';

// These icons are not used in the current Hero section based on the screenshot,
// but keeping them in case they are used elsewhere or intended for future use.
const CheckIcon = () => (
  <svg className="h-3 w-3 text-lynx-medium mr-1.5 flex-shrink-0 mt-0.5" viewBox="0 0 12 12" fill="none">
    <path d="M2 6l3 3 5-6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);
const PlusIcon = () => (
  <svg className="h-3 w-3 text-lynx-primary" viewBox="0 0 12 12" fill="none">
    <path d="M6 2v8M2 6h8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
  </svg>
);
const BuildingIcon = () => (
  <svg className="h-3 w-3 text-lynx-primary" viewBox="0 0 12 12" fill="none">
    <path d="M2 10V3a1 1 0 011-1h6a1 1 0 011 1v7M3 10h6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
    <path d="M4 4h1m-1 2h1m2-2h1m-1 2h1" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
  </svg>
);

const Hero: React.FC = () => {
  const navigate = useNavigate();
  
  // Mock data for quick demo access
  const handleDemoBrowseTeachers = () => {
    const mockStudentPreferences = {
      instrument: 'Piano',
      genres: ['Classical', 'Jazz', 'Pop'],
      skillLevel: 'Intermediate',
      lessonLocation: ['Online', 'At the teacher\'s studio'],
      age: '18 and above',
      learnerType: 'learner' as const,
      email: 'demo@example.com'
    };

    console.log('Navigating to browse teachers demo with mock data');
    navigate('/browse-teachers', { 
      state: { 
        studentPreferences: mockStudentPreferences 
      } 
    });
  };

  const handleDemoTeacherDetail = () => {
    const mockTeacher = {
      id: '1',
      name: 'Sarah Johnson',
      instruments: ['Piano', 'Voice/Vocals'],
      specialties: ['Classical', 'Jazz', 'Pop'],
      rating: 4.9,
      totalReviews: 127,
      hourlyRate: 75,
      location: 'Downtown',
      distance: '2.5 miles',
      bio: 'Professional pianist and vocalist with 15+ years of teaching experience. Specializes in classical technique and contemporary performance.',
      experience: '15+ years',
      availability: ['Weekdays', 'Evenings'],
      lessonFormats: ['Online', 'On-site (teacher\'s studio)', 'In-home (student\'s home)']
    };

    const mockStudentPreferences = {
      instrument: 'Piano',
      genres: ['Classical', 'Jazz', 'Pop'],
      skillLevel: 'Intermediate',
      lessonLocation: ['Online', 'At the teacher\'s studio'],
      age: '18 and above',
      learnerType: 'learner' as const,
      email: 'demo@example.com'
    };

    console.log('Navigating to teacher detail demo with mock data');
    navigate('/teacher-detail', { 
      state: { 
        teacher: mockTeacher,
        studentPreferences: mockStudentPreferences 
      } 
    });
  };

  console.log('Rendering Hero component with updated styles');
  return (
    <section className="min-h-screen bg-gradient-to-b from-white to-blue-50 pt-20"> {/* Adjusted pt for fixed navbar, changed gradient to light blue */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col justify-center items-center text-center min-h-[calc(100vh-5rem)]"> {/* max-w-4xl for tighter content */}
        <span className="uppercase font-semibold tracking-wider text-blue-700 mb-3 text-sm">
          Transform Your Musical Journey
        </span>
        <h1 className="text-5xl sm:text-6xl lg:text-7xl font-extrabold text-gray-900 mb-6 tracking-tight">
          Learn & Teach Music Online
        </h1>
        <p className="max-w-2xl text-lg text-gray-600 leading-relaxed mb-10">
          Whether you're looking to learn or ready to teach, LessonLynx connects passionate musicians in perfect harmony.
        </p>
        <div className="mt-10 flex items-center justify-center gap-x-6">
          <Link
            to="/instrument-selection"
            className="rounded-md bg-blue-600 px-3.5 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-blue-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600"
          >
            Find a Teacher
          </Link>
          <Link
            to="/for-teachers"
            className="text-sm font-semibold leading-6 text-gray-900 hover:text-blue-600"
          >
            Become a Teacher <span aria-hidden="true">→</span>
          </Link>
        </div>
        
        {/* Demo Section */}
        <div className="mt-12 p-8 bg-white rounded-2xl shadow-lg border border-gray-100 max-w-4xl w-full">
          <h3 className="text-2xl font-bold text-gray-900 mb-2 text-center">🎯 Test the Platform</h3>
          <p className="text-gray-600 text-center mb-6">Experience all the features we've built - no signup required!</p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
            
            {/* Student Experience */}
            <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
              <h4 className="font-semibold text-blue-900 mb-3">👨‍🎓 Student Experience</h4>
              <div className="space-y-2">
                <button
                  onClick={handleDemoBrowseTeachers}
                  className="w-full px-4 py-2 text-sm text-blue-700 bg-blue-100 hover:bg-blue-200 rounded-lg transition-colors"
                >
                  🔍 Browse Teachers
                </button>
                <button
                  onClick={handleDemoTeacherDetail}
                  className="w-full px-4 py-2 text-sm text-blue-700 bg-blue-100 hover:bg-blue-200 rounded-lg transition-colors"
                >
                  👩‍🏫 Teacher Profile
                </button>
                <button
                  onClick={() => {
                    console.log('Navigating to booking demo');
                    navigate('/book-lesson', { 
                      state: { 
                        teacher: {
                          id: '1',
                          name: 'Sarah Johnson',
                          instruments: ['Piano'],
                          hourlyRate: 75,
                          rating: 4.9,
                          totalReviews: 127
                        },
                        studentPreferences: {
                          instrument: 'Piano',
                          skillLevel: 'Intermediate'
                        }
                      } 
                    });
                  }}
                  className="w-full px-4 py-2 text-sm text-blue-700 bg-blue-100 hover:bg-blue-200 rounded-lg transition-colors"
                >
                  📅 Book a Lesson
                </button>
                <button
                  onClick={() => {
                    const mockStudentData = {
                      firstName: 'Emma',
                      lastName: 'Thompson',
                      email: 'emma.thompson@example.com',
                      phoneNumber: '(555) 987-6543',
                      instrument: 'Piano',
                      skillLevel: 'Beginner',
                      genres: ['Classical', 'Pop'],
                      learningGoals: ['Learn to read sheet music', 'Play favorite songs'],
                      lessonLocation: ['Online', 'At the teacher\'s studio'],
                      age: '12 years old',
                      learnerType: 'learner',
                      guardianInfo: {
                        name: 'Sarah Thompson',
                        email: 'sarah.thompson@example.com',
                        phone: '(555) 123-4567'
                      }
                    };
                    console.log('Navigating to student dashboard demo');
                    navigate('/student-dashboard', { state: { studentData: mockStudentData } });
                  }}
                  className="w-full px-4 py-2 text-sm text-blue-700 bg-blue-100 hover:bg-blue-200 rounded-lg transition-colors"
                >
                  📊 Student Dashboard
                </button>
              </div>
            </div>

            {/* Teacher Experience */}
            <div className="p-4 bg-green-50 rounded-lg border border-green-200">
              <h4 className="font-semibold text-green-900 mb-3">👩‍🏫 Teacher Experience</h4>
              <div className="space-y-2">
                <button
                  onClick={() => {
                    console.log('Navigating to teacher questionnaire demo');
                    navigate('/teacher-questionnaire');
                  }}
                  className="w-full px-4 py-2 text-sm text-green-700 bg-green-100 hover:bg-green-200 rounded-lg transition-colors"
                >
                  📝 Complete Profile
                </button>
                <button
                  onClick={() => {
                    const mockTeacherData = {
                      firstName: 'Michael',
                      lastName: 'Rodriguez',
                      email: 'michael@example.com',
                      phoneNumber: '(555) 123-4567',
                      instrumentsTaught: ['Piano', 'Voice/Vocals'],
                      specialtyInstrument: 'Piano',
                      experience: '10-15 years',
                      agesTaught: ['Children (6-12)', 'Teens (13-17)', 'Adults (18+)'],
                      specializedAgeGroup: 'All ages',
                      teachingSpecialties: ['Classical', 'Jazz', 'Popular music', 'Music theory'],
                      specializedTeachingAreas: ['Performance preparation', 'Music theory'],
                      languagesSpoken: ['English', 'Spanish'],
                      locationsOffered: ['Online', 'On-site (teacher\'s studio)']
                    };
                    console.log('Navigating to teacher dashboard demo');
                    navigate('/teacher-dashboard', { state: { teacherData: mockTeacherData } });
                  }}
                  className="w-full px-4 py-2 text-sm text-green-700 bg-green-100 hover:bg-green-200 rounded-lg transition-colors"
                >
                  📊 Teacher Dashboard
                </button>
                <button
                  onClick={() => {
                    const mockTeacherData = {
                      firstName: 'Michael',
                      lastName: 'Rodriguez',
                      email: 'michael@example.com',
                      phoneNumber: '(555) 123-4567',
                      instrumentsTaught: ['Piano', 'Voice/Vocals'],
                      specialtyInstrument: 'Piano',
                      experience: '10-15 years',
                      agesTaught: ['Children (6-12)', 'Teens (13-17)', 'Adults (18+)'],
                      specializedAgeGroup: 'All ages',
                      teachingSpecialties: ['Classical', 'Jazz', 'Popular music', 'Music theory'],
                      specializedTeachingAreas: ['Performance preparation', 'Music theory'],
                      languagesSpoken: ['English', 'Spanish'],
                      locationsOffered: ['Online', 'On-site (teacher\'s studio)']
                    };
                    console.log('Navigating to teacher profile demo');
                    navigate('/teacher-profile', { state: { teacherData: mockTeacherData } });
                  }}
                  className="w-full px-4 py-2 text-sm text-green-700 bg-green-100 hover:bg-green-200 rounded-lg transition-colors"
                >
                  👤 Teacher Profile
                </button>
                <button
                  onClick={() => {
                    console.log('Navigating to calendar demo');
                    navigate('/calendar-demo');
                  }}
                  className="w-full px-4 py-2 text-sm text-green-700 bg-green-100 hover:bg-green-200 rounded-lg transition-colors"
                >
                  📅 Calendar Demo
                </button>
              </div>
            </div>

            {/* Communication */}
            <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
              <h4 className="font-semibold text-purple-900 mb-3">💬 Communication</h4>
              <div className="space-y-2">
                <button
                  onClick={() => {
                    console.log('Navigating to messaging demo');
                    navigate('/messages');
                  }}
                  className="w-full px-4 py-2 text-sm text-purple-700 bg-purple-100 hover:bg-purple-200 rounded-lg transition-colors"
                >
                  💬 Messaging System
                </button>
                <button
                  onClick={() => {
                    console.log('Navigating to messaging with student context');
                    navigate('/messages', { 
                      state: { 
                        openConversationWith: 'Emma Thompson',
                        userType: 'student'
                      } 
                    });
                  }}
                  className="w-full px-4 py-2 text-sm text-purple-700 bg-purple-100 hover:bg-purple-200 rounded-lg transition-colors"
                >
                  🎯 Open Chat
                </button>
                <button
                  onClick={() => {
                    console.log('Navigating to booking confirmation demo');
                    navigate('/booking-confirmation', {
                      state: {
                        bookingData: {
                          teacher: { name: 'Sarah Johnson', rating: 4.9 },
                          lessonDetails: {
                            type: 'Online',
                            date: 'Monday',
                            time: '4:00 PM',
                            duration: 30,
                            price: 37.50
                          },
                          specialRequests: 'Focus on classical pieces'
                        },
                        referenceNumber: 'LLX' + Date.now()
                      }
                    });
                  }}
                  className="w-full px-4 py-2 text-sm text-purple-700 bg-purple-100 hover:bg-purple-200 rounded-lg transition-colors"
                >
                  ✅ Booking Confirmation
                </button>
                <button
                  onClick={() => {
                    const mockTeacher = {
                      id: '1',
                      name: 'Sarah Johnson',
                      hourlyRate: 75,
                      rating: 4.9,
                      totalReviews: 127,
                      instruments: ['Piano']
                    };
                    const mockBookingData = {
                      selectedDate: 'Monday, March 25th',
                      selectedTime: '4:00 PM',
                      duration: 30,
                      lessonType: 'Online',
                      instrument: 'Piano'
                    };
                    console.log('Navigating to payment demo');
                    navigate('/payment', {
                      state: {
                        teacher: mockTeacher,
                        bookingData: mockBookingData,
                        studentData: {
                          firstName: 'Emma',
                          lastName: 'Thompson',
                          email: 'emma@example.com'
                        }
                      }
                    });
                  }}
                  className="w-full px-4 py-2 text-sm text-purple-700 bg-purple-100 hover:bg-purple-200 rounded-lg transition-colors"
                >
                  💳 Secure Payment
                </button>
              </div>
            </div>
          </div>

          <div className="text-center">
            <p className="text-sm text-gray-500 mb-2">🔥 Complete end-to-end experience:</p>
            <p className="text-xs text-gray-400">
              Browse → Book → Teacher Reviews → Approve → Message → Manage Lessons
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero; 